import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface AuditLogTableProps {
  logs: any[];
  loading: boolean;
}

const AuditLogTable: React.FC<AuditLogTableProps> = ({ logs, loading }) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (logId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const formatActionType = (type: string): string => {
    return t(`warehouse.auditLog.actionTypes.${type}`, { defaultValue: type });
  };

  const formatChangesJson = (changesJson: any): React.ReactNode => {
    if (!changesJson) return <span className="text-gray-400">-</span>;

    try {
      const changes = typeof changesJson === 'string' ? JSON.parse(changesJson) : changesJson;
      return (
        <div className="space-y-1">
          {Object.entries(changes).map(([key, value]) => (
            <div key={key} className="text-sm">
              <span className="font-medium text-gray-700">{key}:</span>{' '}
              <span className="text-gray-900">{String(value)}</span>
            </div>
          ))}
        </div>
      );
    } catch {
      return <span className="text-gray-600">{String(changesJson)}</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2">{t('common.loading')}</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        {t('warehouse.auditLog.noLogs')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('warehouse.auditLog.actionTime')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('warehouse.auditLog.actor')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('warehouse.auditLog.actionType')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('warehouse.auditLog.warehouse')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('warehouse.auditLog.item')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {logs.map((log) => {
            const isExpanded = expandedRows.has(log.id);
            return (
              <React.Fragment key={log.id}>
                <tr
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleRow(log.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(log.actionTime).toLocaleString('vi-VN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.actorName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {formatActionType(log.actionType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {log.warehouseName ? (
                      <>
                        {log.warehouseName}
                        <span className="text-gray-500 text-xs ml-1">
                          ({log.warehouseCode})
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {log.itemName || <span className="text-gray-400">-</span>}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="px-6 py-4">
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              {t('warehouse.auditLog.changes')}
                            </h4>
                            {formatChangesJson(log.changesJson)}
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              {t('warehouse.auditLog.otherInfo')}
                            </h4>
                            <div className="space-y-1 text-sm">
                              {log.transactionId && (
                                <div>
                                  <span className="font-medium text-gray-700">
                                    {t('warehouse.auditLog.relatedTransaction')}:
                                  </span>{' '}
                                  <span className="text-gray-900">#{log.transactionId}</span>
                                </div>
                              )}
                              <div>
                                <span className="font-medium text-gray-700">{t('warehouse.auditLog.ipAddress')}:</span>{' '}
                                <span className="text-gray-900">
                                  {log.ipAddress || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">ID:</span>{' '}
                                <span className="text-gray-900">#{log.id}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AuditLogTable;
